import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';

const testimonials = [
  {
    name: 'Mariana S.',
    role: 'Aprovada em Concurso Federal',
    text: 'O FocusTrack transformou minha rotina de estudos. O sistema de revisões espaçadas me ajudou a reter muito mais conteúdo. Passei no concurso na primeira tentativa!',
    stars: 5,
  },
  {
    name: 'Lucas P.',
    role: 'Estudante de Medicina',
    text: 'O timer Pomodoro e as estatísticas detalhadas me deram uma visão clara do meu progresso. Consegui aumentar minha taxa de acertos em 40% em dois meses.',
    stars: 5,
  },
  {
    name: 'Ana Clara R.',
    role: 'Aprovada na OAB',
    text: 'Com o plano de estudos por pesos, organizei minhas disciplinas de forma equilibrada. A recomendação por IA é incrível para saber exatamente o que estudar a cada dia.',
    stars: 5,
  },
  {
    name: 'Rafael M.',
    role: 'Concurso TRF - 2ª Região',
    text: 'Estudava sem método e perdia muito tempo. Com o FocusTrack, criei um cronograma realista e consegui manter a disciplina. Fui aprovado com uma das melhores notas!',
    stars: 5,
  },
  {
    name: 'Camila F.',
    role: 'Residência Médica - USP',
    text: 'As revisões espaçadas foram um divisor de águas pra mim. Nunca mais esqueci matéria de prova. O sistema de conquistas me manteve motivada nos dias difíceis.',
    stars: 5,
  },
  {
    name: 'Pedro H.',
    role: 'Aprovado no ENEM - Eng. Computação',
    text: 'Usei o FocusTrack nos últimos 6 meses antes do ENEM. O controle de questões e a análise de desempenho por disciplina me mostraram exatamente onde eu precisava melhorar.',
    stars: 5,
  },
  {
    name: 'Juliana T.',
    role: 'Concurso Receita Federal',
    text: 'A funcionalidade de simulados me ajudou a treinar sob pressão. Fiz mais de 50 simulados pelo app e no dia da prova me senti super preparada. Recomendo demais!',
    stars: 5,
  },
  {
    name: 'Diego A.',
    role: 'Estudante de Direito',
    text: 'O cronograma inteligente por IA se adapta à minha rotina e prioriza as matérias que mais preciso. É como ter um coach particular de estudos no bolso.',
    stars: 4,
  },
  {
    name: 'Beatriz L.',
    role: 'Aprovada no Concurso da PF',
    text: 'Consegui estudar 12 disciplinas de forma organizada sem surtar. O FocusTrack me deu clareza sobre quanto tempo dedicar a cada matéria. Resultado: aprovação!',
    stars: 5,
  },
];

const ITEMS_PER_PAGE = 3;
const AUTO_PLAY_INTERVAL = 6000;

export function TestimonialsCarousel() {
  const totalPages = Math.ceil(testimonials.length / ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(1);

  const goToPage = useCallback((page: number, dir?: number) => {
    setDirection(dir ?? (page > currentPage ? 1 : -1));
    setCurrentPage(page);
  }, [currentPage]);

  const next = useCallback(() => {
    goToPage((currentPage + 1) % totalPages, 1);
  }, [currentPage, totalPages, goToPage]);

  const prev = useCallback(() => {
    goToPage((currentPage - 1 + totalPages) % totalPages, -1);
  }, [currentPage, totalPages, goToPage]);

  useEffect(() => {
    const timer = setInterval(next, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  const currentTestimonials = testimonials.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0,
    }),
  };

  return (
    <section className="py-20 overflow-hidden">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold md:text-4xl">
            O que nossos <span className="text-primary">estudantes</span> dizem
          </h2>
          <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
            Veja como o FocusTrack está ajudando estudantes a conquistar seus objetivos.
          </p>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          {/* Navigation buttons */}
          <Button
            variant="outline"
            size="icon"
            onClick={prev}
            aria-label="Depoimento anterior"
            className="absolute -left-4 lg:-left-14 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md hidden md:flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => { next(); }}
            aria-label="Próximo depoimento"
            className="absolute -right-4 lg:-right-14 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md hidden md:flex"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          {/* Cards */}
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: "easeInOut" }}
              className="grid gap-8 md:grid-cols-3"
            >
              {currentTestimonials.map((testimonial, index) => (
                <motion.div
                  key={`${currentPage}-${index}`}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.12 }}
                >
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="p-6 flex flex-col h-full relative">
                      <Quote className="absolute top-4 right-4 h-8 w-8 text-primary/10" />
                      <div className="flex gap-1 mb-4">
                        {Array.from({ length: testimonial.stars }).map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      <p className="text-muted-foreground text-sm flex-1 mb-4 leading-relaxed">
                        "{testimonial.text}"
                      </p>
                      <div className="border-t pt-4">
                        <p className="font-semibold text-sm">{testimonial.name}</p>
                        <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                aria-label={`Ir para página ${i + 1} de depoimentos`}
                aria-current={i === currentPage}
                className="p-2 -m-1 inline-flex items-center justify-center bg-transparent"
              >
                <span
                  className={`block h-2.5 rounded-full transition-all duration-300 ${
                    i === currentPage
                      ? 'w-8 bg-primary'
                      : 'w-2.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Mobile swipe hint */}
          <div className="flex justify-center gap-3 mt-4 md:hidden">
            <Button variant="ghost" size="sm" onClick={prev}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
            </Button>
            <Button variant="ghost" size="sm" onClick={() => next()}>
              Próximo <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
